import type { FormInstance } from 'antd/lib/form';
import { addMinutes, isAfter, isBefore } from 'date-fns';
import type { Topic } from 'models';
import { useTranslation } from 'react-i18next';
import {
  Col,
  DateInput,
  type DatePickerDateType,
  Form,
  Input,
  Row,
  SelectInput,
  Title,
  getDatePickerDateType,
  getDateType,
  message,
} from 'ui';

export type ActivityFormStep1Values = {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- TODO: Needs to be fixed later
  end_time: Date;
  name: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- TODO: Needs to be fixed later
  programs_topic_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- TODO: Needs to be fixed later
  start_time: Date;
};

type P = {
  endTime?: Date;
  form: FormInstance;
  startTime?: Date;
  topics: Topic[];
};

const ActivityFormStep1 = ({ endTime, form, startTime, topics }: P) => {
  const { t } = useTranslation();

  return (
    <div className="activity-form-step-1">
      <Title level={3} marginBottom>
        Activity details
      </Title>
      <SelectInput
        input={{
          getOptionLabel: (option) => option.name,
          getOptionValue: (option) => option.id,
          options: topics,
          placeholder: t('Please select the module the activity belongs to'),
        }}
        item={{
          label: 'Program module',
          name: 'programs_topic_id',
          rules: [
            {
              required: true,
            },
          ],
        }}
      />
      <Row gutter="ms">
        <Col span={12}>
          <DateInput
            input={{
              minimum: new Date(),
              showTime: true,
            }}
            item={{
              getValueFromEvent(newStartTime: DatePickerDateType): DatePickerDateType {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                const formEndTime = form.getFieldValue('end_time') as DatePickerDateType;

                if (isAfter(getDateType(newStartTime), addMinutes(getDateType(formEndTime), -60))) {
                  message.warning('End time was changed to 1 hour later according to start time');
                  form.setFieldsValue({
                    end_time: getDatePickerDateType(addMinutes(getDateType(newStartTime), 60)),
                  });
                }

                return newStartTime;
              },
              label: 'Starts on',
              name: 'start_time',
              rules: [
                {
                  required: true,
                },
                {
                  // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
                  async validator(_, value: DatePickerDateType) {
                    if (startTime && isBefore(getDateType(value), startTime)) {
                      throw new Error(t('Error: Activity start time must be set after Program start time'));
                    }
                  },
                },
              ],
            }}
          />
        </Col>
        <Col span={12}>
          <DateInput
            input={{
              minimum: new Date(),
              showTime: true,
            }}
            item={{
              getValueFromEvent(newEndTime: DatePickerDateType): DatePickerDateType {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                const formStartTime = form.getFieldValue('start_time') as DatePickerDateType;

                if (isBefore(getDateType(newEndTime), addMinutes(getDateType(formStartTime), 60))) {
                  message.warning('End time was changed to 1 hour later according to start time');
                  return getDatePickerDateType(addMinutes(getDateType(formStartTime), 60));
                }

                return newEndTime;
              },
              label: 'Ends on',
              name: 'end_time',
              rules: [
                {
                  required: true,
                },
                {
                  // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
                  async validator(_, value: DatePickerDateType) {
                    if (endTime && isAfter(getDateType(value), endTime)) {
                      throw new Error(t('Error: Activity end time must be set before Program end time'));
                    }
                  },
                },
              ],
            }}
          />
        </Col>
      </Row>
      <Form.Item<ActivityFormStep1Values>
        label="Activity name"
        name="name"
        rules={[{ required: true, whitespace: true }]}
        style={{ marginBottom: 0 }}
      >
        <Input />
      </Form.Item>
    </div>
  );
};

export default ActivityFormStep1;
